import React, { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { Row } from "@/shared/ui/Blocks";
import { Button, ButtonProps } from "@/shared/ui/Button";
import { Icon, IconName } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";

interface SelectButtonProps extends Omit<ButtonProps, "size"> {
  placeholder?: string;
  value?: string;
  "data-state"?: "open" | "closed";
  size?: "lg" | "md" | "sm" | "xs";
  icon?: IconName;
}

const selectButtonStyles = cva(
  "w-full p-4 rounded-md border border-grey-300 border-solid",
  {
    variants: {
      size: {
        lg: "h-[48px]",
        md: "h-[40px]",
        sm: "h-[32px] w-[140px] border-transparent hover:border-solid hover:border-grey-200",
        xs: "h-[30px] px-3 hover:border-grey-400",
      },
    },
    defaultVariants: {
      size: "lg",
    },
  }
);

const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(
  (props, ref) => {
    const { placeholder, value, size = "lg", icon, ...rest } = props;

    const isOpen = rest?.["data-state"] === "open";

    const textClasses = cn("text-sm text-grey-500");

    return (
      <Button
        variant="ghost"
        {...rest}
        ref={ref}
        className={cn(selectButtonStyles({ size }), rest.className)}
      >
        <Row className="flex-1 items-center justify-between space-x-1">
          {icon && <Icon icon={icon} color="black" size="sm" />}
          <p
            className={cn(textClasses, value && "font-semibold text-grey-700")}
          >
            {value || placeholder}
          </p>
          <Icon
            icon="ChevronDown"
            size="sm"
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </Row>
      </Button>
    );
  }
);

SelectButton.displayName = "SelectButton";

export { SelectButton };
